import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Accordion } from "react-bootstrap";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus, FaStethoscope, FaSearch, FaFolder, FaFolderOpen, FaFileAlt } from "react-icons/fa";
import moment from "moment";
import { MdAccessTime, MdDateRange } from "react-icons/md";

export default function PetPhysicalExamComponent({ appointmentId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [petPhysicalExams, setPetPhysicalExams] = useState([]);
    const [physicalExams, setPhysicalExams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingExam, setEditingExam] = useState(null);
    const [deletingExamId, setDeletingExamId] = useState(null);
    const [selectedExams, setSelectedExams] = useState([]);
    const [examNotes, setExamNotes] = useState({});
    const [generalNote, setGeneralNote] = useState("");
    const [existingNoteId, setExistingNoteId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [openAccordion, setOpenAccordion] = useState(null);
    const [hasExamForAppointment, setHasExamForAppointment] = useState(false);


    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];
    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    useEffect(() => {
        fetchPetPhysicalExams();
    }, [petId, appointmentId]);

    const fetchPetPhysicalExams = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-physical-exam/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.success) {
                setPetPhysicalExams(response.data.petPhysicalExam || []);
                setPhysicalExams(response.data.physicalExam || []);
                // Check if current appointment has an exam
                const hasExam = (response.data.petPhysicalExam || []).some(exam => String(exam?.appointment_id) === String(appointmentId));
                setHasExamForAppointment(hasExam);
            }
        } catch (error) {
            console.error("Error fetching pet physical exams:", error);
            alert(t('failed_to_load_physical_exams'));
            setPetPhysicalExams([]);
            setPhysicalExams([]);
            setHasExamForAppointment(false);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return {
            date: date.toLocaleDateString(),
            time: date.toLocaleTimeString(),
            full: date.toLocaleString()
        };
    };

    const handleAddExam = () => {
        setEditingExam(null);
        setSelectedExams([]);
        setExamNotes({});
        setGeneralNote("");
        setExistingNoteId(null);
        setSearchTerm("");
        setShowModal(true);
    };

    const handleEditExam = (exam) => {
        setEditingExam(exam);

        const examText = exam.physical_exam || '';
        const selected = [];
        const notes = {};

        // Extract general note from the note array
        if (exam.note && Array.isArray(exam.note) && exam.note.length > 0) {
            setGeneralNote(exam.note[0].note || "");
            setExistingNoteId(exam.note[0].id);
        } else {
            setGeneralNote("");
            setExistingNoteId(null);
        }

        // Parse the physical_exam text to find which items are selected
        if (physicalExams && physicalExams.length > 0 && examText) {
            // Split the exam text by new lines
            const lines = examText.split('\n').filter(line => line.trim());

            lines.forEach(line => {
                if (line.includes(':')) {
                    const parts = line.split(':');
                    if (parts.length >= 2) {
                        const parentName = parts[0].trim();
                        const childName = parts[1].trim();
                        const note = parts.length > 2 ? parts.slice(2).join(':').trim() : '';

                        // Find the parent and child
                        for (const parent of physicalExams) {
                            if (parent.name === parentName && parent.child_physical_exam && parent.child_physical_exam.length > 0) {
                                const foundChild = parent.child_physical_exam.find(child => child.name === childName);
                                if (foundChild) {
                                    selected.push({
                                        id: foundChild.id,
                                        parent_id: parent.id,
                                        parent_name: parent.name,
                                        child_name: foundChild.name
                                    });
                                    if (note) {
                                        notes[foundChild.id] = note;
                                    }
                                    break;
                                }
                            }
                        }
                    }
                }
            });
        }

        setSelectedExams(selected);
        setExamNotes(notes);
        setSearchTerm("");
        setShowModal(true);
    };

    const handleDeleteClick = (examId) => {
        setDeletingExamId(examId);
        setShowDeleteModal(true);
    };

    const handleDeleteExam = async () => {
        try {
            setSubmitting(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/pet-physical-exam-delete/${deletingExamId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.success) {
                setShowDeleteModal(false);
                fetchPetPhysicalExams();
            }
        } catch (error) {
            console.error("Error deleting physical exam:", error);
            // alert(t('failed_to_delete_physical_exam'));
        } finally {
            setSubmitting(false);
            setDeletingExamId(null);
        }
    };

    const handleCheckboxChange = (examId, parentId, parentName, childName) => {
        setSelectedExams(prev => {
            const isSelected = prev.some(item => item.id === examId);

            if (isSelected) {
                const newNotes = { ...examNotes };
                delete newNotes[examId];
                setExamNotes(newNotes);
                return prev.filter(item => item.id !== examId);
            } else {
                return [...prev, {
                    id: examId,
                    parent_id: parentId,
                    parent_name: parentName,
                    child_name: childName
                }];
            }
        });
    };

    const handleNoteChange = (examId, note) => {
        setExamNotes(prev => ({
            ...prev,
            [examId]: note
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (selectedExams.length === 0) {
            // alert(t('please_select_at_least_one_physical_exam'));
            return;
        }

        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");

            let examText = '';

            // Build exam text from selectedExams with parent name prefix
            selectedExams.forEach(selectedItem => {
                const note = examNotes[selectedItem.id] || '';
                // FIX: Include parent name before child name
                if (note && note.trim()) {
                    examText += `${selectedItem.parent_name}: ${selectedItem.child_name}: ${note}\n`;
                } else {
                    examText += `${selectedItem.parent_name}: ${selectedItem.child_name}\n`;
                }
            });

            const url = editingExam
                ? `${process.env.REACT_APP_API_URL}/pet-physical-exam-update/${editingExam.id}`
                : `${process.env.REACT_APP_API_URL}/pet-physical-exam`;

            const payload = {
                appointment_id: appointmentId,
                physical_exam: examText.trim(),
            };

            // Add note fields if note exists
            if (generalNote && generalNote.trim()) {
                payload.note = generalNote.trim();
                if (existingNoteId) {
                    payload.note_id = existingNoteId;
                }
            }

            console.log("Sending payload:", payload);

            const response = await axios({
                method: 'post',
                url: url,
                data: payload,
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (response.data.success) {
                setShowModal(false);
                fetchPetPhysicalExams();
            }
        } catch (error) {
            console.error("Error saving physical exam:", error);
            alert(error.response?.data?.message || t('failed_to_save_physical_exam'));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading physical exams')}</p>
            </div>
        );
    }

    const parentExams = physicalExams && physicalExams.length > 0
        ? physicalExams.filter(e => e && e.parent_id === null)
        : [];

    const filteredParentExams = parentExams.filter(parent => {
        if (!searchTerm) return true;

        const searchLower = searchTerm.toLowerCase();

        if (parent.name.toLowerCase().includes(searchLower)) return true;

        if (parent.child_physical_exam && parent.child_physical_exam.some(child =>
            child.name.toLowerCase().includes(searchLower)
        )) return true;

        return false;
    });

    const getSelectedExamsByParent = () => {
        const grouped = {};

        if (selectedExams.length > 0) {
            selectedExams.forEach(selectedItem => {
                if (!grouped[selectedItem.parent_id]) {
                    grouped[selectedItem.parent_id] = {
                        parentName: selectedItem.parent_name,
                        children: []
                    };
                }
                grouped[selectedItem.parent_id].children.push({
                    id: selectedItem.id,
                    name: selectedItem.child_name,
                    note: examNotes[selectedItem.id] || ''
                });
            });
        }

        return grouped;
    };

    const selectedExamsByParent = getSelectedExamsByParent();

    // Create a map for quick lookup of child exam names to their parent info
    const getChildParentMap = () => {
        const map = {};
        physicalExams.forEach(parent => {
            if (parent.child_physical_exam) {
                parent.child_physical_exam.forEach(child => {
                    map[child.id] = {
                        parent_id: parent.id,
                        parent_name: parent.name,
                        child_name: child.name
                    };
                });
            }
        });
        return map;
    };

    const childParentMap = getChildParentMap();

    return (
        <div>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h6 className="mb-0 text-muted">{t('Physical Exams List')}</h6>

                {isAppointmentOpen && !hasExamForAppointment && hasPermission("Appointment Exam Create") && (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleAddExam}
                        className="d-flex align-items-center gap-2"
                    >
                        <FaPlus size={12} /> {t('add physical exam')}
                    </Button>
                )}
            </div>

            {!petPhysicalExams || petPhysicalExams.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                    <FaStethoscope size={30} className="mb-2 text-muted" />
                    <p className="mb-0">{t('no physical exams found')}</p>
                </Alert>
            ) : (
                <Accordion
                    activeKey={openAccordion}
                    onSelect={(key) => setOpenAccordion(key === openAccordion ? null : key)}
                    className="pet-physical-exam-accordion"
                >
                    {petPhysicalExams.map((exam) => {
                        if (!exam) return null;
                        const dateTime = formatDateTime(exam.created_at);
                        const noteText = exam.note && Array.isArray(exam.note) && exam.note.length > 0
                            ? exam.note[0].note
                            : null;

                        // Parse the physical_exam text for display
                        const examLines = exam.physical_exam ? exam.physical_exam.split('\n').filter(line => line.trim()) : [];

                        return (
                            <Accordion.Item eventKey={String(exam.id)} key={exam.id} className="mb-3 border rounded">
                                <Accordion.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                            <div className="text-muted small">
                                                <strong> {exam.appointment?.appointment_number || 'Unknown Appointment'}</strong>
                                            </div>
                                            <div className="text-muted small">
                                                👨‍⚕️<strong>Doctor</strong>: {exam.doctor?.name || 'Unknown Doctor'}
                                            </div>
                                            <div className="d-flex align-items-center gap-3 text-muted small">
                                                <span className="d-flex align-items-center gap-1">
                                                    <MdDateRange size={14} />
                                                    {moment(dateTime.date).format("DD MMM YYYY")}
                                                </span>

                                                <span className="d-flex align-items-center gap-1">
                                                    <MdAccessTime size={14} />
                                                    {moment(dateTime.time, "HH:mm:ss").format("hh:mm A")}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                                            {hasPermission("Appointment Exam edit") && (
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => handleEditExam(exam)}
                                                    className="d-flex align-items-center gap-1"
                                                    style={{ background: '#3182ce', color: '#fff' }}
                                                >
                                                    <FaEdit size={12} />
                                                    <span className="d-none d-sm-inline">{t('edit')}</span>
                                                </Button>
                                            )}
                                            {/* <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleDeleteClick(exam.id)}
                                                className="d-flex align-items-center gap-1 bg-danger"
                                            >
                                                <FaTrash size={12} />
                                                <span className="d-none d-sm-inline">{t('delete')}</span>
                                            </Button> */}
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="bg-white">
                                    <Row className="g-4">
                                        <Col md={12}>
                                            <div className="border-bottom pb-2 mb-3">
                                                <h6 className="mb-0 d-flex align-items-center gap-2">
                                                    <FaStethoscope className="text-primary" />
                                                    {t('physical exam details')}
                                                </h6>
                                            </div>
                                            {/* In the Accordion.Body display section */}
                                            <div className="p-3 bg-light rounded">
                                                {examLines.length > 0 ? (
                                                    <>
                                                        {(() => {
                                                            // Group lines by parent
                                                            const groupedByParent = {};

                                                            examLines.forEach(line => {
                                                                if (line.includes(':')) {
                                                                    const parts = line.split(':');
                                                                    if (parts.length >= 2) {
                                                                        const parentName = parts[0].trim();
                                                                        const childName = parts[1].trim();
                                                                        const note = parts.length > 2 ? parts.slice(2).join(':').trim() : '';

                                                                        if (!groupedByParent[parentName]) {
                                                                            groupedByParent[parentName] = [];
                                                                        }
                                                                        groupedByParent[parentName].push({
                                                                            childName,
                                                                            note
                                                                        });
                                                                    }
                                                                }
                                                            });

                                                            return Object.entries(groupedByParent).map(([parentName, children], idx) => (
                                                                <div key={idx} className="mb-3">
                                                                    <div className="d-flex align-items-center gap-2 mb-2">
                                                                        <h6 className="fw-bold text-primary mb-0">{parentName}</h6>
                                                                    </div>
                                                                    <div className="ms-4">
                                                                        {children.map((child, childIdx) => (
                                                                            <div key={childIdx} className="mb-2">
                                                                                <div className="d-flex align-items-start gap-2">
                                                                                    <strong className="text-dark">{child.childName}</strong>
                                                                                    {child.note && (
                                                                                        <span className="text-muted">- {child.note}</span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            ));
                                                        })()}
                                                        {noteText && (
                                                            <div className="mt-3 pt-2 border-top">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <FaFileAlt className="text-success" />
                                                                    <strong className="text-muted">{t('general note')}:</strong>
                                                                </div>
                                                                <p className="mb-0 mt-1 ms-4 text-secondary">{noteText}</p>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <p className="text-muted mb-0">{t('no physical exam details')}</p>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}

            {/* Modal */}
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="xl"
                style={{ '--bs-modal-width': '1400px' }}
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        {editingExam ? t('Edit Physical Exam') : t('Add Physical Exam')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body style={{ padding: '1rem', height: '550px', overflowY: 'auto' }} className="custom-modal-body">
                        <Row>
                            {/* Left Column: Selection */}
                            <Col md={6}>
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-bold">{t('Select Physical Exam')}</Form.Label>
                                    <div className="position-relative">
                                        <Form.Control
                                            type="text"
                                            placeholder={t('Search physical exam...')}
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="mb-2"
                                            size="sm"
                                        />
                                        <FaSearch className="position-absolute" style={{ right: '10px', top: '8px', color: '#999', fontSize: '12px' }} />
                                    </div>
                                    <div
                                        className="border rounded p-2"
                                        style={{ height: '280px', overflowY: 'auto', backgroundColor: '#fff' }}
                                    >
                                        {filteredParentExams.length > 0 ? (
                                            filteredParentExams.map(parent => (
                                                <div key={parent.id} className="mb-2">
                                                    <h6 className="fw-bold text-secondary mb-1" style={{ fontSize: '13px' }}>{parent.name}</h6>
                                                    {parent.child_physical_exam && parent.child_physical_exam.length > 0 ? (
                                                        parent.child_physical_exam
                                                            .filter(child => {
                                                                if (!searchTerm) return true;
                                                                return child.name.toLowerCase().includes(searchTerm.toLowerCase());
                                                            })
                                                            .map(child => (
                                                                <div key={child.id} className="ms-2 mb-1">
                                                                    <Form.Check
                                                                        type="checkbox"
                                                                        id={`child-${child.id}`}
                                                                        checked={selectedExams.some(item => item.id === child.id)}
                                                                        onChange={() => handleCheckboxChange(child.id, parent.id, parent.name, child.name)}
                                                                        label={child.name}
                                                                        style={{ fontSize: '13px' }}
                                                                    />
                                                                    {selectedExams.some(item => item.id === child.id) && (
                                                                        <Form.Control
                                                                            type="text"
                                                                            size="sm"
                                                                            className="mt-1"
                                                                            placeholder={t('Add note...')}
                                                                            value={examNotes[child.id] || ''}
                                                                            onChange={(e) => handleNoteChange(child.id, e.target.value)}
                                                                        />
                                                                    )}
                                                                </div>
                                                            ))
                                                    ) : (
                                                        <p className="text-muted small ms-2">No child exams available</p>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-muted text-center">{t('No matching exams found')}</p>
                                        )}
                                    </div>
                                </Form.Group>
                            </Col>

                            {/* Right Column: Preview/Selected */}
                            <Col md={6}>
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-bold">{t('Selected Physical Exam')}</Form.Label>
                                    <div
                                        className="border rounded p-2 bg-white"
                                        style={{ height: '280px', overflowY: 'auto' }}
                                    >
                                        {Object.keys(selectedExamsByParent).length > 0 ? (
                                            Object.values(selectedExamsByParent).map((group, index) => (
                                                <div key={index} className="mb-2">
                                                    <h6 className="fw-bold text-primary mb-1" style={{ fontSize: '13px' }}>{group.parentName}</h6>
                                                    {group.children.map((child, childIndex) => (
                                                        <div key={childIndex} className="ms-2 mb-1">
                                                            <div className="text-dark" style={{ fontSize: '13px' }}>
                                                                • {child.name}
                                                                {child.note && (
                                                                    <span className="text-muted ms-1">
                                                                        - {child.note}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-muted text-center">{t('No exam selected')}</p>
                                        )}
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* General Note Field */}
                        <Form.Group className="mt-3">
                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                <FaFileAlt /> {t('General Note')}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={generalNote}
                                onChange={(e) => setGeneralNote(e.target.value)}
                                placeholder={t('Add any additional notes here...')}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer style={{ padding: '0.75rem' }} className="custom-modal-footer">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setShowModal(false)}
                        >
                            {t('Close')}
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            size="sm"
                            disabled={submitting}
                        >
                            {submitting ? <Spinner size="sm" /> : t('Submit')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Delete Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">{t('confirm_delete')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="custom-modal-body">
                    <FaTrash size={48} className="text-danger mb-3" />
                    <h5>{t('delete_physical_exam_confirmation')}</h5>
                    <p className="text-muted mb-0">{t('this_action_cannot_be_undone')}</p>
                </Modal.Body>
                <Modal.Footer className="custom-modal-footer">
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>{t('cancel')}</Button>
                    <Button variant="danger" onClick={handleDeleteExam} disabled={submitting}>
                        {submitting ? t('deleting') : t('delete')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}